import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, eligibility, photos, programs } from '@cwos/api-client';
import {
  Avatar,
  Pill,
  ScopeNote,
  SuccessEmpty,
  TableCard,
  useTabListKeys,
  type Column,
} from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import { listPhase } from '../listState.js';
import { pickerState, type PickerState } from '../pickerState.js';
import { ListState } from '../ListStates.js';
import { LoadFailure } from '../LoadFailure.js';
import { scopeNoteFor, statedPopulation } from '../readScope.js';
import { useNav } from '../navigation.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  EMPTY_HOLD_DRAFT,
  REASON_CATEGORY_READING,
  REASON_WITHHELD_EXPLANATION,
  RESTORATION_READING,
  SCOPE_READING,
  formatWhen,
  holdPayload,
  messageOf,
  placedByReading,
  policyOffWarning,
  reasonCellText,
  reasonReading,
  restorationPill,
  summarize,
  validateHoldDraft,
  type HoldDraft,
} from './eligibilityLogic.js';

/**
 * Eligibility — who in this organization is currently held back from service
 * work, why, and what it would take to restore them.
 *
 * WHY THIS SCREEN EXISTS AT ALL. `eligibilityGuard` has been registered ahead
 * of eight route families in production since migration 1758110000000. A worker
 * under a hold has been meeting a 403 on the clock, the schedule, service
 * delivery, documentation, mileage, EVV, timesheets and their caseload — and
 * there was no surface anywhere in the product that listed who was held, showed
 * why, or lifted one. The capability was complete and the door was missing;
 * this is the door.
 *
 * THE REASON IS NOT ON THIS SCREEN FOR EVERYBODY. `directory:read` opens the
 * list; the four reason fields need `directory:manage`, and the server sends
 * `reasonWithheld: true` to say the difference. A hold whose reason is withheld
 * renders as "Withheld" and never as a blank — see `reasonReading`.
 *
 * THE PERMISSIONS ARE BORROWED AND INTERIM. `directory:*` is what the routes
 * check today; the domain proposes `eligibility:*` keys it has not been given.
 * The gates here name the same constants the client module re-exports, so when
 * the keys land, one edit moves both sides.
 */

const TABS = [
  { key: 'active' as const, label: 'Active holds' },
  { key: 'lifted' as const, label: 'Lifted' },
];

export function EligibilityCenter() {
  const qc = useQueryClient();
  const permissions = usePermissions();
  const navigate = useNav();
  /**
   * FOUR ANSWERS, NOT TWO. The note at the bottom of the policy card names a
   * permission the reader is missing, and `has()` is `false` for three
   * different reasons. This key is NOT the key the route is gated on — that is
   * `directory:read` — so a reader whose cached set carries `directory:read`
   * walks through `RequirePermission` under `isError` and is told, from a set
   * nobody could refresh, that changing the policy is not theirs to do.
   *
   * `canManage` itself stays default-deny: it gates two `enabled:` flags and
   * three `disabled=` attributes, and those must all stay closed until the
   * answer is yes.
   */
  const manageGate = authorityGate(permissions, eligibility.ELIGIBILITY_MANAGE);
  const canManage = gateOpen(manageGate);
  /**
   * THE ROSTER IS A SECOND DOOR, AND `eligibility:manage` IS NOT ITS KEY.
   *
   * `GET /directory/workers` refuses rather than narrows — both gates are
   * `can()` at organization scope — so a reader who may place a hold and may
   * not read the workforce directory got a 403 here. The form then said "We
   * couldn't load the roster … Nothing about your access has changed", which is
   * the opposite of what happened: the refusal WAS about their access, and the
   * sentence talked them out of the one action that would fix it.
   *
   * It also cached that 403 under the SHARED `['directory', 'workers']` key
   * that `Scheduling`, `Visits`, `Timesheets`, `CreateChannel` and
   * `MemberManager` all read and all gate. Asking a question those five
   * declined to ask answered it wrongly for them too.
   */
  const canListWorkers = mayListWorkers(permissions);

  const [tab, setTab] = useState<'active' | 'lifted'>('active');
  const tabKeys = useTabListKeys<HTMLDivElement>();
  const [placing, setPlacing] = useState(false);
  const [draft, setDraft] = useState<HoldDraft>(EMPTY_HOLD_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [policyDraft, setPolicyDraft] = useState<eligibility.EligibilityPolicy | null>(null);
  const [policyError, setPolicyError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['eligibility', 'holds', tab],
    queryFn: () => eligibility.holds({ status: tab }),
  });

  const policy = useQuery({
    queryKey: ['eligibility', 'policy'],
    queryFn: () => eligibility.policy(),
  });

  // Only while the form is open, and only for somebody who could submit it. An
  // organization with no reason to name a worker never pays for the request.
  const workerOptions = useQuery({
    queryKey: ['directory', 'workers'],
    queryFn: () => directory.workers(),
    enabled: canManage && placing && canListWorkers,
  });

  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ limit: 200 }),
    enabled: canManage && placing && draft.scopeType === 'program',
  });

  const place = useMutation({
    mutationFn: () => eligibility.placeHold(holdPayload(draft)),
    onSuccess: () => {
      setPlacing(false);
      setDraft(EMPTY_HOLD_DRAFT);
      setFormError(null);
      void qc.invalidateQueries({ queryKey: ['eligibility'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const savePolicy = useMutation({
    mutationFn: (next: eligibility.EligibilityPolicy) => eligibility.setPolicy(next),
    onSuccess: () => {
      setPolicyDraft(null);
      setPolicyError(null);
      void qc.invalidateQueries({ queryKey: ['eligibility', 'policy'] });
    },
    onError: (err: unknown) => setPolicyError(messageOf(err)),
  });

  const errors = validateHoldDraft(draft);
  const canSubmit = Object.keys(errors).length === 0;

  /**
   * The refusal claimed only from a RESOLVED set, the shape the org-chart
   * pickers use: `usePermissions` reads empty while it loads and after it
   * fails, so `!canListWorkers` on its own would tell a reader whose set never
   * arrived that the roster is withheld from them.
   */
  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canListWorkers;
  const workerPicker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workerOptions,
          workerOptions.data?.length ?? 0,
        ),
    'workers',
    'Choose a worker…',
  );

  const holds = list.data?.holds ?? [];
  const phase = listPhase(list, holds.length);
  const summary = summarize(holds);

  /**
   * WHOSE HOLDS THESE ARE — TAKEN FROM THE SERVER, never re-derived here.
   *
   * `HoldList.scope` is set by the same predicate that narrowed the rows, so
   * the frame and the rows cannot disagree. The client must not recompute it
   * from `usePermissions`: `/auth/me` flattens `scopeType`/`scopeId` away, so a
   * program-scoped `directory:read_all` would read there as organization-wide
   * and this screen would present one supervisor's three reports as the state
   * of the workforce.
   */
  const population = statedPopulation(
    list.data === undefined
      ? undefined
      : list.data.scope === 'organization'
        ? 'organization'
        : 'caseload',
    'the people you supervise',
  );
  const note = scopeNoteFor(population);

  const columns: Column<eligibility.HoldListRow>[] = [
    {
      key: 'person',
      header: 'Person',
      render: (row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-8)' }}>
          <Avatar
            name={row.displayName}
            src={photos.personPhotoSrc({
              userId: row.userId,
              photoUpdatedAt: row.photoUpdatedAt,
            })}
            size={24}
          />
          {row.displayName}
        </span>
      ),
    },
    { key: 'scope', header: 'Reaches', render: (row) => SCOPE_READING[row.scopeType] },
    {
      key: 'reason',
      header: 'Reason',
      render: (row) => {
        const reading = reasonReading(row);
        return reading.kind === 'withheld' ? (
          <span className="rn-muted" title={REASON_WITHHELD_EXPLANATION}>
            Withheld
          </span>
        ) : (
          reasonCellText(reading)
        );
      },
    },
    {
      key: 'restoration',
      header: 'Restores',
      render: (row) => {
        const pill = restorationPill(row);
        return (
          <Pill variant={pill.variant} dot>
            {pill.label}
          </Pill>
        );
      },
    },
    { key: 'placedBy', header: 'Placed by', render: (row) => placedByReading(row) },
    {
      key: 'when',
      header: tab === 'active' ? 'Placed' : 'Lifted',
      render: (row) => formatWhen(tab === 'active' ? row.placedAt : row.liftedAt),
    },
  ];

  const policyView = policyDraft ?? policy.data ?? null;
  const offWarning = policyView === null ? null : policyOffWarning(policyView);

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Eligibility"
        blurb="Who is currently held back from service work, why, and what would restore them. A hold is not the same thing as employment — a person can be employed and ineligible, and on their way back."
      />

      {/* The summary. Every figure is over the rows that arrived and says so —
          there is deliberately no workforce denominator, because the list is
          scoped and a percentage over a differently scoped total is true of no
          population. */}
      <SettingsCard>
        <h2 className="rn-card-title">
          {tab === 'active' ? 'Holds in force' : 'Holds that have been lifted'}
        </h2>
        {phase.kind === 'refused' ? (
          <p className="rn-muted">
            Your account cannot see who is on hold, so there is nothing to count here. This is an
            answer, not an empty organization.
          </p>
        ) : phase.kind === 'loading' ? (
          <p className="rn-muted">Counting…</p>
        ) : phase.kind === 'failed' ? (
          <p className="rn-muted">
            We couldn’t count these just now. Do not read this as “nobody is on hold”.
          </p>
        ) : (
          <div className="rn-row" style={{ gap: 'var(--space-24)', flexWrap: 'wrap' }}>
            <Figure label="Holds" value={summary.total} />
            <Figure label="Need a person to lift" value={summary.needsAPerson} />
            <Figure label="Clear themselves" value={summary.clearsItself} />
            <Figure label="Placed by Phibian" value={summary.placedBySystem} />
          </div>
        )}
        {note ? <ScopeNote scope={note} /> : null}
      </SettingsCard>

      <SettingsCard>
        <div className="cmp-control-head">
          {/* The roving tabindex, Home/End and arrow handling come from the
              shared hook — a tablist that wires its own has none of them, and
              the strip then makes a promise to assistive technology that the
              keyboard does not keep. */}
          <div role="tablist" aria-label="Hold status" className="cmp-tabs" {...tabKeys}>
            {TABS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                id={`eligibility-tab-${entry.key}`}
                aria-controls="eligibility-panel"
                aria-selected={tab === entry.key}
                className="rn-tab-btn"
                onClick={() => setTab(entry.key)}
              >
                {entry.label}
              </button>
            ))}
          </div>
          {canManage ? (
            <button
              type="button"
              className="rn-btn"
              onClick={() => {
                setPlacing((open) => !open);
                setFormError(null);
              }}
            >
              {placing ? 'Cancel' : 'Place a hold'}
            </button>
          ) : null}
        </div>

        {placing && canManage ? (
          <HoldForm
            draft={draft}
            errors={errors}
            formError={formError}
            canSubmit={canSubmit && !place.isPending}
            workers={workerOptions.data ?? []}
            workerPicker={workerPicker}
            programList={programOptions.data?.programs ?? []}
            onChange={setDraft}
            onSubmit={() => place.mutate()}
          />
        ) : null}

        <div id="eligibility-panel" role="tabpanel" aria-labelledby={`eligibility-tab-${tab}`}>
          <ListState
            phase={phase}
            surface="table"
            rows={5}
            failedMessage={
              tab === 'active'
                ? 'We couldn’t load who is on hold just now. Do not read this as an empty window — somebody may be restricted and not shown here.'
                : 'We couldn’t load lifted holds just now.'
            }
            onRetry={() => void list.refetch()}
            empty={
              tab === 'active' ? (
                <SuccessEmpty
                  heading="Nobody is on hold"
                  message="Every worker you can see is eligible for service work right now. If a worker is being refused in the app, it is not an eligibility hold."
                />
              ) : (
                <SuccessEmpty
                  heading="No hold has been lifted"
                  message="Nothing has been restored yet, because nothing you can see has been held."
                />
              )
            }
          >
            <TableCard
              columns={columns}
              rows={holds}
              rowKey={(row) => row.id}
              onRowOpen={(row) => navigate(`/workforce/eligibility/${row.workerId}`)}
              caption={tab === 'active' ? 'Active eligibility holds' : 'Lifted eligibility holds'}
            />
          </ListState>
        </div>
      </SettingsCard>

      {/* What the organization has asked Phibian to do on its own. An OFF
          switch is a policy choice that makes the list above legitimately
          shorter, and a reader who does not know it is off will read the short
          list as a compliant workforce. So it is stated, not implied. */}
      <SettingsCard>
        <h2 className="rn-card-title">What places a hold automatically</h2>
        {policy.isError ? (
          <LoadFailure
            error={policy.error}
            message="We couldn’t read your automatic-hold policy just now."
            onRetry={() => void policy.refetch()}
          />
        ) : policyView === null ? (
          <p className="rn-muted">Reading your policy…</p>
        ) : (
          <>
            <PolicyToggle
              label="Restore automatically when a requirement is met again"
              hint="With this off, a hold Phibian placed for a lapsed requirement stays until a person lifts it, even after the requirement is satisfied."
              checked={policyView.autoRestoreEnabled}
              disabled={!canManage}
              onChange={(value) => setPolicyDraft({ ...policyView, autoRestoreEnabled: value })}
            />
            <PolicyToggle
              label="Place a hold when a credential lapses"
              checked={policyView.autoHoldOnCredentialLapse}
              disabled={!canManage}
              onChange={(value) =>
                setPolicyDraft({ ...policyView, autoHoldOnCredentialLapse: value })
              }
            />
            <PolicyToggle
              label="Place a hold when supervision is overdue"
              checked={policyView.autoHoldOnSupervisionOverdue}
              disabled={!canManage}
              onChange={(value) =>
                setPolicyDraft({ ...policyView, autoHoldOnSupervisionOverdue: value })
              }
            />
            {offWarning ? (
              <p className="rn-muted" style={{ marginTop: 'var(--space-8)' }}>
                {offWarning}
              </p>
            ) : null}
            {policyError ? (
              <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
                {policyError}
              </p>
            ) : null}
            {canManage && policyDraft !== null ? (
              <div className="rn-row" style={{ gap: 'var(--space-8)' }}>
                <button
                  type="button"
                  className="rn-btn"
                  disabled={savePolicy.isPending}
                  onClick={() => savePolicy.mutate(policyDraft)}
                >
                  Save policy
                </button>
                <button
                  type="button"
                  className="rn-btn-quiet"
                  onClick={() => {
                    setPolicyDraft(null);
                    setPolicyError(null);
                  }}
                >
                  Discard
                </button>
              </div>
            ) : null}
            <GateNotice
              gate={manageGate}
              what="change the eligibility policy"
              onRetry={() => permissions.refetch()}
              refusedNode={
                <p className="rn-muted">
                  Changing this needs the {eligibility.ELIGIBILITY_MANAGE} permission.
                </p>
              }
            />
          </>
        )}
      </SettingsCard>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <span style={{ fontSize: 28, lineHeight: 1 }}>{value}</span>
      <span className="rn-muted">{label}</span>
    </div>
  );
}

function PolicyToggle({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', marginTop: 'var(--space-8)' }}>
      <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'flex-start' }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
        <span>{label}</span>
      </label>
      {hint ? (
        <span className="rn-muted" style={{ fontSize: 13, paddingLeft: 'var(--space-24)' }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The place-a-hold form.
 *
 * The restoration rule is enforced HERE as well as in the database, because
 * `eligibility_hold_restoration_ck` answers a management-shaped category with
 * automatic restoration as a 23514, and an operator cannot decode that. The
 * check is `eligibility.mayRestoreAutomatically`, the client's copy of the same
 * list, so the two cannot drift.
 */
function HoldForm({
  draft,
  errors,
  formError,
  canSubmit,
  workers,
  workerPicker,
  programList,
  onChange,
  onSubmit,
}: {
  draft: HoldDraft;
  errors: Record<string, string>;
  formError: string | null;
  canSubmit: boolean;
  workers: readonly directory.Worker[];
  /**
   * WHY the worker list is what it is — refused, unread, still loading, or
   * real. It replaces a `workersFailed: boolean`, which could only ever say
   * "not loaded" and said it for a 403 as readily as for a dropped connection.
   */
  workerPicker: PickerState;
  programList: readonly programs.Program[];
  onChange: (next: HoldDraft) => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="rn-col"
      style={{ gap: 'var(--space-12)', marginBottom: 'var(--space-16)' }}
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit();
      }}
    >
      <SettingsField label="Worker" error={errors.workerId}>
        <select
          value={draft.workerId}
          disabled={workerPicker.disabled}
          onChange={(e) => onChange({ ...draft, workerId: e.currentTarget.value })}
        >
          <option value="">{workerPicker.placeholder}</option>
          {workers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.displayName}
            </option>
          ))}
        </select>
      </SettingsField>
      {workerPicker.note !== null ? (
        <p className="rn-muted" role="status">
          {workerPicker.note}
        </p>
      ) : null}

      <SettingsField
        label="How far it reaches"
        hint="A hold scoped to one program or one role still refuses every protected request, because most routes do not say which program they are about. Scope is what the hold means, not what it blocks."
      >
        <select
          value={draft.scopeType}
          onChange={(e) =>
            onChange({
              ...draft,
              scopeType: e.currentTarget.value as eligibility.EligibilityScopeType,
            })
          }
        >
          {(['organization', 'program', 'role'] as const).map((scope) => (
            <option key={scope} value={scope}>
              {SCOPE_READING[scope]}
            </option>
          ))}
        </select>
      </SettingsField>

      {draft.scopeType === 'program' ? (
        <SettingsField label="Program" error={errors.scopeProgramId}>
          <select
            value={draft.scopeProgramId}
            onChange={(e) => onChange({ ...draft, scopeProgramId: e.currentTarget.value })}
          >
            <option value="">Choose a program…</option>
            {programList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </SettingsField>
      ) : null}

      {draft.scopeType === 'role' ? (
        <SettingsField label="Role key" error={errors.scopeRoleKey}>
          <input
            value={draft.scopeRoleKey}
            onChange={(e) => onChange({ ...draft, scopeRoleKey: e.currentTarget.value })}
          />
        </SettingsField>
      ) : null}

      <SettingsField label="Reason">
        <select
          value={draft.reasonCategory}
          onChange={(e) =>
            onChange({
              ...draft,
              reasonCategory: e.currentTarget.value as eligibility.ReasonCategory,
            })
          }
        >
          {eligibility.REASON_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {REASON_CATEGORY_READING[category]}
            </option>
          ))}
        </select>
      </SettingsField>

      <SettingsField label="How it is restored" error={errors.restorationMode}>
        <select
          value={draft.restorationMode}
          onChange={(e) =>
            onChange({
              ...draft,
              restorationMode: e.currentTarget.value as eligibility.RestorationMode,
            })
          }
        >
          {(['management_only', 'automatic_when_requirement_verified'] as const).map((mode) => (
            <option key={mode} value={mode}>
              {RESTORATION_READING[mode]}
            </option>
          ))}
        </select>
      </SettingsField>

      <SettingsField
        label="Management note"
        hint="The worker never sees this. They are told only that their account is restricted and to contact their supervisor."
        error={errors.managementReason}
      >
        <textarea
          rows={3}
          value={draft.managementReason}
          onChange={(e) => onChange({ ...draft, managementReason: e.currentTarget.value })}
        />
      </SettingsField>

      {formError ? (
        <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
          {formError}
        </p>
      ) : null}

      <div>
        <button type="submit" className="rn-btn" disabled={!canSubmit}>
          Place the hold
        </button>
      </div>
    </form>
  );
}
